import { Link, useLocation } from "react-router-dom";
import {
    LayoutDashboard,
    Search,
    Eye,
    Bell,
    BarChart3,
    Briefcase,
    ShieldAlert,
    Radio,
    Settings as SettingsIcon,
    Moon,
    Sun,
    Waves,
    PlayCircle,
    LogOut,
} from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";

const NAV = [
    { to: "/", label: "Overview", icon: LayoutDashboard, testid: "nav-overview" },
    { to: "/deals", label: "Deal Finder", icon: Search, testid: "nav-deals" },
    { to: "/market", label: "Market Intelligence", icon: BarChart3, testid: "nav-market" },
    { to: "/committee", label: "Investment Committee", icon: Briefcase, testid: "nav-committee" },
    { to: "/quality", label: "Data Quality", icon: ShieldAlert, testid: "nav-quality" },
    { to: "/watchlist", label: "Watchlist", icon: Eye, testid: "nav-watchlist" },
    { to: "/alerts", label: "Alerts", icon: Bell, testid: "nav-alerts" },
    { to: "/sources", label: "Sources", icon: Radio, testid: "nav-sources" },
    { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

const Layout = ({ children }) => {
    const { theme, toggle } = useTheme();
    const { user, logout } = useAuth();
    const { pathname } = useLocation();
    const [scanning, setScanning] = useState(false);
    const [unreadAlerts, setUnreadAlerts] = useState(0);

    const loadAlertCount = useCallback(async () => {
        try {
            const { data } = await api.get("/alerts", { params: { unread_only: true, limit: 200 } });
            setUnreadAlerts(data.length);
        } catch (e) {
            // The badge polls every 30s — surface failures to the console for
            // debugging but don't spam the user with toasts on transient errors.
            console.warn("loadAlertCount failed:", e?.message || e);
        }
    }, []);

    useEffect(() => {
        loadAlertCount();
        const t = setInterval(loadAlertCount, 30000);
        return () => clearInterval(t);
    }, [loadAlertCount]);

    const runScan = async () => {
        setScanning(true);
        try {
            await api.post("/scan/run", {});
            toast.success("Scan started in background", {
                description:
                    "Real scans take several minutes. Watch the Sources page or Integrity panel for progress.",
            });
        } catch (e) {
            toast.error("Could not start scan", { description: String(e?.message || e) });
        } finally {
            setScanning(false);
        }
    };

    return (
        <div className="min-h-screen flex bg-background text-foreground theme-transition">
            {/* Sidebar */}
            <aside
                className="hidden md:flex flex-col w-64 border-r border-border/70 px-5 py-7 sticky top-0 h-screen"
                data-testid="sidebar"
            >
                <div className="flex items-center gap-3 mb-10">
                    <div className="w-9 h-9 rounded-md flex items-center justify-center bg-primary text-primary-foreground">
                        <Waves className="w-5 h-5" />
                    </div>
                    <div className="leading-tight">
                        <div className="font-grotesk font-bold text-base tracking-tight">
                            Sea View Flip Finder
                        </div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                            Montenegro Intel
                        </div>
                    </div>
                </div>

                <nav className="flex flex-col gap-1 flex-1">
                    {NAV.map(({ to, label, icon: Icon, testid }) => {
                        const active = to === "/" ? pathname === "/" : pathname.startsWith(to);
                        return (
                            <Link
                                key={to}
                                to={to}
                                data-testid={testid}
                                className={`group flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                    active
                                        ? "bg-secondary text-secondary-foreground"
                                        : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                                }`}
                            >
                                <Icon className="w-4 h-4" />
                                <span>{label}</span>
                                {label === "Alerts" && unreadAlerts > 0 && (
                                    <Badge
                                        variant="outline"
                                        className="ml-auto h-5 px-1.5 text-[10px] border-accent text-accent"
                                        data-testid="alerts-unread-badge"
                                    >
                                        {unreadAlerts}
                                    </Badge>
                                )}
                            </Link>
                        );
                    })}
                </nav>

                <div className="mt-6 text-[11px] text-muted-foreground leading-relaxed">
                    <p className="uppercase tracking-[0.18em] mb-1">Data discipline</p>
                    <p>
                        Real data only. No demo listings. Empty states are intentional until
                        scans return verified results.
                    </p>
                </div>
            </aside>

            {/* Main column */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Top bar */}
                <header className="sticky top-0 z-30 backdrop-blur-md bg-background/85 border-b border-border/70">
                    <div className="flex items-center justify-between px-6 py-3 gap-4">
                        <div className="md:hidden flex items-center gap-2">
                            <div className="w-8 h-8 rounded-md flex items-center justify-center bg-primary text-primary-foreground">
                                <Waves className="w-4 h-4" />
                            </div>
                            <span className="font-grotesk font-bold text-sm">
                                Flip Finder
                            </span>
                        </div>
                        <div className="hidden md:block">
                            <h2 className="font-grotesk font-semibold text-base">
                                {NAV.find((n) =>
                                    n.to === "/" ? pathname === "/" : pathname.startsWith(n.to)
                                )?.label || "Dashboard"}
                            </h2>
                            <p className="text-xs text-muted-foreground">
                                Montenegro real-estate deal sourcing
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="default"
                                size="sm"
                                onClick={runScan}
                                disabled={scanning}
                                data-testid="global-scan-button"
                                className="gap-2"
                            >
                                <PlayCircle className="w-4 h-4" />
                                {scanning ? "Scanning…" : "Run scan"}
                            </Button>
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={toggle}
                                data-testid="theme-toggle"
                                aria-label="Toggle theme"
                            >
                                {theme === "dark" ? (
                                    <Sun className="w-4 h-4" />
                                ) : (
                                    <Moon className="w-4 h-4" />
                                )}
                            </Button>
                            <div className="hidden md:flex items-center gap-2 pl-2 ml-1 border-l border-border/60">
                                {user?.email && (
                                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground" data-testid="current-user-email">
                                        {user.email}
                                    </span>
                                )}
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={logout}
                                    data-testid="logout-button"
                                    aria-label="Sign out"
                                    title="Sign out"
                                >
                                    <LogOut className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </header>

                <main className="flex-1 px-4 md:px-8 py-6 md:py-8 max-w-[1500px] w-full mx-auto">
                    {children}
                </main>
            </div>
        </div>
    );
};

export default Layout;
